import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { hasEditableRouting } from './workflow-presentation-authoring';
import {
  insertStepOnWorkflowPath,
  replaceWorkflowPath,
  workflowStepPlacementPaths,
} from './workflow-path-authoring';

/** Suggestions are for new identities only; renaming retained work never calls this. */
export function suggestWorkStepIdentity(
  name: string,
  document: WorkflowDefinitionDocument,
) {
  const stem =
    name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'work-step';
  const base = /^[a-z]/.test(stem) ? stem : `step-${stem}`;
  let key = base,
    suffix = 2;
  while (
    document.nodes.some((n) => n.id === `node.${key}` || n.stepCode === key)
  )
    key = `${base}-${suffix++}`;
  return { id: `node.${key}`, stepCode: key };
}

export function availableStepSources(
  document: WorkflowDefinitionDocument,
): WorkflowDefinitionNode[] {
  if (!hasEditableRouting(document)) return [];
  const placements = workflowStepPlacementPaths(document);
  return document.nodes.filter((item) => {
    if (
      !item ||
      !['entry', 'human_step', 'automated_step'].includes(item.typeCode)
    )
      return false;
    const outgoing = document.edges.filter(
      (edge) => edge?.fromNodeId === item.id,
    );
    if (outgoing.length === 1)
      return placements.some(
        (placement) =>
          placement.edgeId === outgoing[0].id && placement.eligible,
      );
    if (outgoing.length !== 0) return false;
    return document.nodes.some((target) => {
      if (
        !target ||
        !['human_step', 'automated_step', 'terminal'].includes(target.typeCode)
      )
        return false;
      try {
        replaceWorkflowPath(document, {
          id: '',
          fromNodeId: item.id,
          toNodeId: target.id,
        });
        return true;
      } catch {
        return false;
      }
    });
  });
}

/** Adds explicit paths, or inserts into one selected ordinary path while retaining its identity. */
export function appendConnectedWorkStep(
  document: WorkflowDefinitionDocument,
  node: WorkflowDefinitionNode,
  fromNodeId = '',
  toNodeId = '',
): WorkflowDefinitionDocument {
  if (
    document.nodes.some((n) => n.id === node.id || n.stepCode === node.stepCode)
  )
    throw new Error('Choose unused technical keys for this step.');
  if ((fromNodeId || toNodeId) && !hasEditableRouting(document))
    throw new Error(
      'Enable editable routing before connecting this step. You can also add it without paths.',
    );
  if (
    fromNodeId &&
    !availableStepSources(document).some((n) => n.id === fromNodeId)
  )
    throw new Error(
      'That source already has routing or behavior that needs the path editor.',
    );
  const insertion = fromNodeId
    ? document.edges.find((e) => e.fromNodeId === fromNodeId)
    : undefined;
  if (insertion && toNodeId !== insertion.toNodeId)
    throw new Error(
      'Inserting into this path must retain its current destination. Choose the source again to refresh the connection.',
    );
  if (
    toNodeId &&
    !document.nodes.some(
      (n) =>
        n.id === toNodeId &&
        ['human_step', 'automated_step', 'terminal'].includes(n.typeCode),
    )
  )
    throw new Error('Choose an existing work step or completion point.');
  if (insertion) return insertStepOnWorkflowPath(document, node, insertion.id);
  let next = { ...document, nodes: [...document.nodes, node] };
  if (fromNodeId)
    next = replaceWorkflowPath(next, { id: '', fromNodeId, toNodeId: node.id });
  if (toNodeId)
    next = replaceWorkflowPath(next, { id: '', fromNodeId: node.id, toNodeId });
  return next;
}
